import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { signup } from "../utils/api";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { RegisterOtp } from "../redux/LoginSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

const Register = () => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobileno, setMobileno] = useState("");
  const [businessname, setBusinessname] = useState("");
  const [businesstype, setBusinesstype] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({
    username: "",
    email: "",
    mobileno: "",
    businessname: "",
    businesstype: "",
    password: "",
    confirmPassword: "",
  });

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character (any)
    const re =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{8,200}$/;
    return re.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      username: "",
      email: "",
      mobileno: "",
      businessname: "",
      businesstype: "",
      password: "",
      confirmPassword: "",
    };
    let isValid = true;

    // Username validation
    if (!username.trim()) {
      newErrors.username = "Username is required.";
      isValid = false;
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters.";
      isValid = false;
    }

    // Email validation
    if (!email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address.";
      isValid = false;
    }

    // Mobile number validation
    if (!mobileno.trim()) {
      newErrors.mobileno = "Mobile number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(mobileno)) {
      newErrors.mobileno = "Mobile number must be 10 digits.";
      isValid = false;
    }

    // Business name validation
    if (!businessname.trim()) {
      newErrors.businessname = "Business name is required.";
      isValid = false;
    } else if (businessname.length < 3) {
      newErrors.businessname = "Business name must be at least 3 characters.";
      isValid = false;
    }

    // Business type validation
    if (!businesstype.trim()) {
      newErrors.businesstype = "Business type is required.";
      isValid = false;
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character.";
      isValid = false;
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password.";
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) return;

    setLoading(true);
    try {
      const jsonData = JSON.stringify({
        name: username,
        email: email,
        mobile: mobileno,
        business_name: businessname,
        businessType: businesstype,
        password: password,
      });

      const encrypted = encrypt(jsonData);
      const response = await signup(encrypted);
      dispatch(RegisterOtp(response));
      toast.success("Account Created Successfully! Redirecting to OTP page...");
      setTimeout(() => {
        navigate("/registerotp");
      }, 1500);
    } catch (err) {
      console.error("Register Error:", err);
      let errorMessage = "Registration failed. Please try again.";

      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.err && errorData.err.includes("users_email_unique")) {
          errorMessage = "This email is already registered. Please use a different email.";
        } else if (errorData.err) {
          errorMessage = errorData.err;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignInClick = () => navigate("/login");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .register-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #0f0f1a;
        }

        /* ── LEFT PANEL ── */
        .register-left {
          flex: 1;
          display: none;
          position: relative;
          overflow: hidden;
          background: linear-gradient(145deg, #0d0d1f 0%, #1a0a3d 50%, #0d1a3d 100%);
        }

        @media (min-width: 1024px) {
          .register-left { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 3rem; }
        }

        /* Animated mesh orbs */
        .register-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(85px);
          opacity: 0.5;
          pointer-events: none;
          animation: orbFloat 14s ease-in-out infinite;
        }
        .register-orb-1 {
          width: 450px; height: 450px;
          background: radial-gradient(circle, rgba(99,91,255,0.55) 0%, transparent 70%);
          top: -100px; left: -100px;
          animation-duration: 16s;
        }
        .register-orb-2 {
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(139,92,246,0.45) 0%, transparent 70%);
          bottom: -80px; right: -80px;
          animation-duration: 20s;
          animation-direction: reverse;
        }
        .register-orb-3 {
          width: 280px; height: 280px;
          background: radial-gradient(circle, rgba(59,130,246,0.35) 0%, transparent 70%);
          top: 55%; left: 45%; transform: translate(-50%, -50%);
          animation-duration: 22s;
        }

        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -50px) scale(1.05); }
          66% { transform: translate(-30px, 40px) scale(0.95); }
        }

        /* Grid dots overlay */
        .register-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }

        /* Left panel content */
        .left-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 440px;
        }

        .left-brand-icon {
          width: 72px; height: 72px;
          border-radius: 20px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 12px 40px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.2);
          animation: iconGlow 3s ease-in-out infinite;
        }

        @keyframes iconGlow {
          0%, 100% { box-shadow: 0 12px 40px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.2); }
          50% { box-shadow: 0 16px 55px rgba(99,91,255,0.85), inset 0 1px 0 rgba(255,255,255,0.2); }
        }

        .left-title {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1.15;
          letter-spacing: -0.03em;
          background: linear-gradient(135deg, #fff 0%, #c4b5fd 60%, #818cf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 1rem;
        }

        .left-subtitle {
          font-size: 0.95rem;
          color: rgba(148,163,184,0.8);
          line-height: 1.65;
          font-weight: 400;
          margin-bottom: 2.5rem;
        }

        /* Feature pills */
        .feature-pills {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          align-items: flex-start;
          width: 100%;
        }

        .feature-pill {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 1rem;
          width: 100%;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .feature-pill:hover {
          background: rgba(99,91,255,0.1);
          border-color: rgba(99,91,255,0.25);
          transform: translateX(4px);
        }

        .feature-pill-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 1.1rem;
        }

        .feature-pill-text strong {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 1px;
        }
        .feature-pill-text span {
          font-size: 0.72rem;
          color: rgba(148,163,184,0.6);
        }

        /* ── RIGHT PANEL ── */
        .register-right {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 1.25rem;
          background: linear-gradient(160deg, #0f0f1a 0%, #13111f 100%);
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 1024px) {
          .register-right { width: 620px; flex-shrink: 0; overflow-y: auto; }
        }

        .register-right::-webkit-scrollbar {
          width: 4px;
        }
        .register-right::-webkit-scrollbar-track {
          background: transparent;
        }
        .register-right::-webkit-scrollbar-thumb {
          background: rgba(99, 91, 255, 0.2);
          border-radius: 2px;
        }

        /* Right subtle glow */
        .register-right::before {
          content: '';
          position: absolute;
          top: -60px; right: -60px;
          width: 320px; height: 320px;
          background: radial-gradient(circle, rgba(99,91,255,0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .register-card {
          width: 100%;
          max-width: 540px;
          position: relative;
          z-index: 2;
        }

        /* Top brand for mobile */
        .mobile-brand {
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (min-width: 1024px) {
          .mobile-brand { display: none; }
        }

        .mobile-brand-icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 0.75rem;
          box-shadow: 0 8px 24px rgba(99,91,255,0.5);
        }

        /* Heading */
        .register-heading {
          margin-bottom: 0.375rem;
        }

        .register-heading h1 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #f1f5f9;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .register-heading p {
          font-size: 0.875rem;
          color: rgba(148,163,184,0.7);
          margin-top: 0.375rem;
          font-weight: 400;
        }

        /* Divider */
        .register-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(99,91,255,0.3), transparent);
          margin: 1.5rem 0;
        }

        /* Form Layout Row */
        .form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }

        @media (min-width: 640px) {
          .form-row { grid-template-columns: 1fr 1fr; }
        }

        /* Form field */
        .register-field {
          margin-bottom: 1.25rem;
        }

        .register-field.full-width {
          grid-column: 1 / -1;
        }

        .register-field label {
          display: block;
          font-size: 0.76rem;
          font-weight: 600;
          color: rgba(148,163,184,0.8);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-bottom: 0.45rem;
        }

        .register-input-wrap {
          position: relative;
        }

        .register-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(148,163,184,0.4);
          font-size: 1.05rem !important;
          pointer-events: none;
        }

        .register-input, .register-select {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.875rem;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          font-size: 0.9rem;
          color: #f1f5f9;
          font-family: 'Inter', sans-serif;
          transition: all 0.25s ease;
          outline: none;
        }

        .register-select {
          appearance: none;
          cursor: pointer;
        }

        .register-select option {
          background: #13111f;
          color: #f1f5f9;
        }

        .select-caret {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(148,163,184,0.5);
          font-size: 1.2rem !important;
          pointer-events: none;
        }

        .register-input::placeholder { color: rgba(148,163,184,0.35); }

        .register-input:focus, .register-select:focus {
          border-color: rgba(99,91,255,0.6);
          background: rgba(99,91,255,0.06);
          box-shadow: 0 0 0 3px rgba(99,91,255,0.12);
        }

        .register-input.error, .register-select.error {
          border-color: rgba(239,68,68,0.5);
          background: rgba(239,68,68,0.04);
        }

        .register-eye-btn {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: rgba(148,163,184,0.45);
          display: flex;
          align-items: center;
          padding: 0;
          transition: color 0.2s;
        }

        .register-eye-btn:hover { color: rgba(148,163,184,0.85); }

        .register-error-msg {
          font-size: 0.72rem;
          color: #f87171;
          margin-top: 0.375rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          line-height: 1.3;
        }

        /* Submit button */
        .register-submit-btn {
          width: 100%;
          padding: 0.9rem 1.5rem;
          background: linear-gradient(135deg, #635bff 0%, #7c3aed 100%);
          border: none;
          border-radius: 0.875rem;
          color: white;
          font-size: 0.9rem;
          font-weight: 700;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 8px 24px rgba(99,91,255,0.45), inset 0 1px 0 rgba(255,255,255,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          letter-spacing: 0.01em;
          position: relative;
          overflow: hidden;
          margin-top: 1.5rem;
        }

        .register-submit-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .register-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.15);
        }

        .register-submit-btn:active:not(:disabled) { transform: translateY(0px); }

        .register-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Spinner */
        .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* Login link */
        .register-login-row {
          text-align: center;
          margin-top: 1.5rem;
          font-size: 0.82rem;
          color: rgba(148, 163, 184, 0.55);
        }

        .register-login-row button {
          background: none;
          border: none;
          font-size: 0.82rem;
          font-weight: 700;
          color: #818cf8;
          cursor: pointer;
          margin-left: 0.25rem;
          font-family: 'Inter', sans-serif;
          transition: color 0.2s;
        }

        .register-login-row button:hover { color: #a78bfa; }

        /* Security badge */
        .register-security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          margin-top: 1.25rem;
          font-size: 0.7rem;
          color: rgba(148,163,184,0.35);
          font-weight: 500;
          letter-spacing: 0.03em;
        }
      `}</style>

      <div className="register-root">

        {/* ── LEFT PANEL ── */}
        <div className="register-left">
          <div className="register-grid" />
          <div className="register-orb register-orb-1" />
          <div className="register-orb register-orb-2" />
          <div className="register-orb register-orb-3" />

          <div className="left-content">
            <div className="left-brand-icon">
              <span className="material-icons" style={{ fontSize: "2rem", color: "white" }}>payments</span>
            </div>

            <div className="left-title">Gaadiaa<br />Trusted Payments</div>

            <p className="left-subtitle">
              Start your business journey with Gaadiaa. Accept payments securely, process disbursements instantly, and scale without boundaries.
            </p>

            <div className="feature-pills">
              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(99,91,255,0.2)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#818cf8" }}>shield</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Bank-Grade Security</strong>
                  <span>256-bit SSL encryption on all transactions</span>
                </div>
              </div>

              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(16,185,129,0.15)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#34d399" }}>bolt</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Instant Settlements</strong>
                  <span>Real-time payout processing 24/7</span>
                </div>
              </div>

              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(245,158,11,0.15)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#fbbf24" }}>analytics</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Smart Dashboard</strong>
                  <span>Full visibility into your financial operations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="register-right">
          <div className="register-card">

            {/* Mobile-only brand */}
            <div className="mobile-brand">
              <div className="mobile-brand-icon">
                <span className="material-icons" style={{ fontSize: "1.5rem", color: "white" }}>payments</span>
              </div>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Gaadiaa</div>
              <div style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.5)", fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "2px" }}>Trusted Payments</div>
            </div>

            {/* Heading */}
            <div className="register-heading">
              <h1>Create your account</h1>
              <p>Register as a merchant and access your sandbox</p>
            </div>

            <div className="register-divider" />

            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="form-row">
                
                {/* Username */}
                <div className="register-field full-width">
                  <label>Full Name <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">person</span>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your full name"
                      className={`register-input ${errors.username ? "error" : ""}`}
                    />
                  </div>
                  {errors.username && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.username}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="register-field">
                  <label>Email Address <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">mail</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@company.com"
                      className={`register-input ${errors.email ? "error" : ""}`}
                    />
                  </div>
                  {errors.email && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Mobile No */}
                <div className="register-field">
                  <label>Mobile Number <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">phone</span>
                    <input
                      type="tel"
                      value={mobileno}
                      onChange={(e) => setMobileno(e.target.value.replace(/[^0-9]/g, ""))}
                      maxLength="10"
                      placeholder="10-digit number"
                      className={`register-input ${errors.mobileno ? "error" : ""}`}
                    />
                  </div>
                  {errors.mobileno && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.mobileno}
                    </p>
                  )}
                </div>

                {/* Business Name */}
                <div className="register-field">
                  <label>Business Name <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">business</span>
                    <input
                      type="text"
                      value={businessname}
                      onChange={(e) => setBusinessname(e.target.value)}
                      placeholder="e.g. Acme Corp"
                      className={`register-input ${errors.businessname ? "error" : ""}`}
                    />
                  </div>
                  {errors.businessname && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.businessname}
                    </p>
                  )}
                </div>

                {/* Business Type */}
                <div className="register-field">
                  <label>Business Type <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">category</span>
                    <select
                      value={businesstype}
                      onChange={(e) => setBusinesstype(e.target.value)}
                      className={`register-select ${errors.businesstype ? "error" : ""}`}
                    >
                      <option value="" disabled>Select Type</option>
                      <option value="1">Individual</option>
                      <option value="2">LLP</option>
                      <option value="3">Partnership</option>
                      <option value="4">Private Limited</option>
                      <option value="5">Proprietorship</option>
                    </select>
                    <span className="material-icons select-caret">expand_more</span>
                  </div>
                  {errors.businesstype && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.businesstype}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="register-field">
                  <label>Password <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">lock</span>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create secure password"
                      className={`register-input ${errors.password ? "error" : ""}`}
                      style={{ paddingRight: "3rem" }}
                    />
                    <button
                      type="button"
                      className="register-eye-btn"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="register-field">
                  <label>Confirm Password <span style={{ color: "#f87171" }}>*</span></label>
                  <div className="register-input-wrap">
                    <span className="material-icons register-input-icon">lock_reset</span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Verify password"
                      className={`register-input ${errors.confirmPassword ? "error" : ""}`}
                      style={{ paddingRight: "3rem" }}
                    />
                    <button
                      type="button"
                      className="register-eye-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="register-error-msg">
                      <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

              </div>

              {/* Submit */}
              <button
                type="submit"
                className="register-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner" />
                    Creating account…
                  </>
                ) : (
                  <>
                    Create Account
                    <span className="material-icons" style={{ fontSize: "1.1rem" }}>arrow_forward</span>
                  </>
                )}
              </button>
            </form>

            {/* Login Link */}
            <div className="register-login-row">
              Already have an account?
              <button onClick={handleSignInClick}>Sign in</button>
            </div>

            {/* Security Badge */}
            <div className="register-security-badge">
              <span className="material-icons" style={{ fontSize: "0.85rem" }}>lock</span>
              Secured with 256-bit SSL encryption
            </div>

          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        toastStyle={{ background: "#1e1b4b", color: "#e2e8f0", border: "1px solid rgba(99,91,255,0.2)" }}
      />
    </>
  );
};

export default Register;
